import { FormikProps } from 'formik';
import { Button } from '@/components/ui/button';
import { Plus, Trash2 } from 'lucide-react';
import { OptionCard } from './OptionCard';
import { QuestionFormValues } from '@/utils/forms/questionTransformers';
import type { SelectedFixtureData } from './types';

interface QuestionOptionsSectionProps {
  formik: FormikProps<QuestionFormValues>;
  selectedTeams?: string[];
  selectedFixtureData?: SelectedFixtureData | null;
  translateField: (
    sourceKey: string,
    sourceValue: string,
    targetKey: string,
    setFieldValue: any
  ) => void;
  isFieldTranslating: (key: string) => boolean;
  isEditable: boolean;
  /** When set, auto-selects this league and disables the league selector in picker modals */
  restrictedLeagueId?: string;
  /** Pickem ID for image upload organization */
  pickemId?: string;
  /** Contest League IP flag — drives licensed vs generic image auto-fill */
  leagueIp?: boolean;
}

export function QuestionOptionsSection({
  formik,
  selectedTeams = [],
  selectedFixtureData = null,
  translateField,
  isFieldTranslating,
  isEditable,
  restrictedLeagueId,
  pickemId,
  leagueIp = false,
}: QuestionOptionsSectionProps) {
  const { errors, touched, values } = formik;

  // Tiebreaker questions don't have options
  if (values.question_type === 'tiebreaker') {
    return null;
  }

  const isCustom = values.question_type === 'custom';
  const isPlayerSelection = values.question_type === 'player';
  const isTeamSelection = values.question_type === 'team';
  const supportsMultipleOptions = isCustom || isPlayerSelection || isTeamSelection;

  const handleAddOption = () => {
    const newOption = {
      option_text_en: '',
      option_text_fr: undefined,
      option_image_en: undefined,
      option_image_fr: undefined,
      third_image_en: undefined,
      third_image_fr: undefined,
      start_utc: undefined,
      is_correct: false,
      display_order: values.options.length,
    };
    formik.setFieldValue('options', [...values.options, newOption]);
  };

  const handleRemoveOption = (index: number) => {
    const newOptions = values.options.filter((_, i) => i !== index);
    formik.setFieldValue('options', newOptions);
  };

  return (
    <div className="border-t pt-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold">Answer Options</h3>
        {typeof errors.options === 'string' && touched.options && (
          <span className="text-sm text-red-500">{errors.options}</span>
        )}
      </div>

      <div className="space-y-4">
        {values.options.map((_, index) => (
          <div key={index} className="relative">
            {supportsMultipleOptions && isEditable && values.options.length > 2 && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute -top-3 -right-3 z-10 h-8 w-8 text-destructive hover:bg-destructive/10"
                onClick={() => handleRemoveOption(index)}
                data-testid={`question-remove-option-${index}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
            <OptionCard
              index={index}
              formik={formik}
              selectedTeams={selectedTeams}
              selectedFixtureData={selectedFixtureData}
              translateField={translateField}
              isFieldTranslating={isFieldTranslating}
              isEditable={isEditable} // Disable editing for over_under
              questionValues={values}
              restrictedLeagueId={restrictedLeagueId}
              pickemId={pickemId}
              leagueIp={leagueIp}
            />
          </div>
        ))}

        {supportsMultipleOptions && isEditable && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddOption}
            data-testid="question-add-option-button"
            className="w-full sm:w-auto"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add option
          </Button>
        )}
      </div>
    </div>
  );
}
